// ---------------------------------------------------------------------------
// DashedDonut
// ---------------------------------------------------------------------------
// Progresso dos cards de visão geral como donut tracejado.
// Cada dash representa um input. A cor reflete o estado individual
// (crítico / atenção / não enviado / entregue). Os dashes são ordenados
// por severidade para que a "concentração de problemas" apareça como
// um arco contínuo no topo do donut.
//
// Limite prático: até ~60 inputs cada dash ainda é distinguível; acima
// disso o donut começa a parecer um anel contínuo.
// ---------------------------------------------------------------------------

import React from "react";
import PropTypes from "prop-types";
import { grayColor } from "../../../assets/jss/material-dashboard-pro-react";
import { INPUT_STATE_COLORS } from "../../../services/inputsV3StateRules";

// Cores canonicas — vindas de ``inputsV3StateRules.js``, MESMA fonte
// usada pelo pip do ``InputBoxV3`` e pela barra do ``ModelingProgressBandV3``.
// Os 9 estados do classificador V3 estao todos representados aqui;
// estado desconhecido cai em cinza (fallback).
//
// ``INPUT_STATE_COLORS`` e sempre uma COR SOLIDA — o donut pinta o
// ``stroke`` do <circle>, que nao aceita gradientes/hachurados via CSS,
// entao mesmo ``pending_decision`` (hachurado warning+danger no card)
// aparece aqui como sua cor solida representativa (laranja).
const DASH_STATE_COLORS = INPUT_STATE_COLORS;

// Ordem de severidade (0 = mais critico, aparece primeiro no arco).
//
// ``system_error`` divide o topo com ``critical``: ambos sao "bloqueado
// / precisa intervir", apenas com origem diferente (falha tecnica vs
// rejeicao). ``in_progress_attention`` fica entre ``attention`` e
// ``processing`` — e parcial com sinal amarelo, severidade intermediaria.
const DASH_SEVERITY = {
  system_error: 0,
  critical: 1,
  pending_decision: 2,
  attention: 3,
  in_progress_attention: 4,
  processing: 5,
  in_progress: 6,
  not_sent: 7,
  delivered_ok: 8,
};

const DashedDonut = ({ states, size = 104, strokeWidth = 8 }) => {
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - strokeWidth) / 2;
  const C = 2 * Math.PI * r;
  const N = states.length;

  if (N === 0) {
    return (
      <svg width={size} height={size} aria-hidden="true">
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={grayColor[5]}
          strokeWidth={strokeWidth}
        />
      </svg>
    );
  }

  const severityOf = (s) => (DASH_SEVERITY[s] != null ? DASH_SEVERITY[s] : 99);
  const sorted = [...states].sort((a, b) => severityOf(a) - severityOf(b));
  const segLen = C / N;
  // Gap com cap absoluto de ~5° (evita gaps gigantes quando N é pequeno) e
  // cap relativo de 28% do slot (preserva o equilíbrio quando N é grande).
  const maxAbsGap = (5 / 360) * C;
  const gapLen = Math.min(segLen * 0.28, maxAbsGap);
  const dashLen = Math.max(2, segLen - gapLen);

  return (
    <svg width={size} height={size} aria-hidden="true">
      {sorted.map((state, i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={DASH_STATE_COLORS[state] || grayColor[5]}
          strokeWidth={strokeWidth}
          strokeDasharray={`${dashLen} ${C - dashLen}`}
          strokeLinecap="butt"
          transform={`rotate(${-90 + (i / N) * 360} ${cx} ${cy})`}
        />
      ))}
    </svg>
  );
};

DashedDonut.propTypes = {
  states: PropTypes.arrayOf(PropTypes.string).isRequired,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
};

export default DashedDonut;
